<script lang="ts">
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import { ButtonVariant } from "@components/common/ButtonVariant.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Form from "@components/form/Form.svelte";
	import Input from "@components/form/Input.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { expectErrorMessage } from "@core/utils/expectErrorMessage.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./PasswordReset.translations.json" with { type: "json" };

	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);

	const isDesktop = createReactiveBreakpoint(">md");
	const { emailForReset, searchParams }: { emailForReset: string; searchParams?: URLSearchParams } = $props();
	const email = $derived(searchParams?.get("email") ?? emailForReset);
	const passwordReset = $derived(createPromiseWithStatus(Auth.confirmForgotPassword.bind(Auth, tenant.locale, email)));

	function validatePasswordsMatch(form: HTMLFormElement): void {
		const passwordAgain = form.elements.namedItem("passwordAgain") as HTMLInputElement;
		const password = form.elements.namedItem("password") as HTMLInputElement;

		if (passwordAgain.value !== password.value && passwordAgain.value.length > 0 && password.value.length > 0) {
			passwordAgain.setCustomValidity(translate("mismatch"));
			password.setCustomValidity(translate("mismatch"));
		} else {
			passwordAgain.setCustomValidity("");
			password.setCustomValidity("");
		}
	}

	async function handlePasswordReset(_event: SubmitEvent, form: HTMLFormElement): Promise<void> {
		validatePasswordsMatch(form);
		if (!form.reportValidity()) {
			return;
		}
		const formData = new FormData(form);

		const password = formData.get("password") as string;
		const code = formData.get("code") as string;

		await passwordReset.call(code, password);
		Auth.closeAuthenticationModal();
	}

	function validateOnInput(event: Event): void {
		const form = (event.currentTarget as HTMLInputElement).closest("form");
		if (form) {
			validatePasswordsMatch(form);
		}
	}

	const error = $derived(
		passwordReset.value.status === "error" &&
			expectErrorMessage(passwordReset.value.error, tenant.locale, {}, translate("unknownError")),
	);
</script>

<Form class="flex flex-col items-stretch gap-4 mt-2 md:mt-0" onsubmit={handlePasswordReset}>
	<p class="text-fcrgray text-sm/6 md:text-base max-w-127">
		<Formatted text={translate("enterNewPassword")} />
		<span class="text-fcrdarkgray">{email}</span>
	</p>
	<Input
		autocomplete="off"
		required
		name="code"
		value={searchParams?.get("code") ?? ""}
		placeholder={translate("confirmationCode")}
	/>
	<Input
		autocomplete="new-password"
		required
		minlength={7}
		name="password"
		oninput={validateOnInput}
		placeholder={translate("placeholderPassword")}
		type="password"
	/>
	<Input
		autocomplete="new-password"
		required
		minlength={7}
		name="passwordAgain"
		oninput={validateOnInput}
		placeholder={translate("placeholderPasswordAgain")}
		type="password"
	/>
	<Button
		loading={passwordReset.value.status === "loading"}
		variant={ButtonVariant.Dark}
		size={isDesktop.current ? ButtonSize.Large : ButtonSize.Medium}
	>
		<Formatted text={translate("resetButton")} />
	</Button>
	{#if error}
		<p class="text-sm text-fcrerror mt-2 [&_a]:text-fcrbrown">
			<Formatted text={error} />
		</p>
	{/if}
</Form>
